/*
 * Entrance / exit motion for the Driver Leaderboard, ported from the design
 * handoff (Leaderboard - Table.dc.html `play()`/`playOut()`). Uses the Web
 * Animations API on elements tagged with `data-anim="title|table|thead|row"`.
 * No-ops where `Element.animate` is unavailable (e.g. jsdom in tests).
 * `animateTitle` is false for page turns within a range, so the title phrase
 * only slot-reels when the date range actually changes.
 */

type Anim = 'title' | 'table' | 'thead' | 'row';

const EASE_IN = 'cubic-bezier(0.16, 1, 0.3, 1)';
const EASE_OUT = 'cubic-bezier(0.7, 0, 0.84, 0)';

const supportsAnimate = (el: Element): boolean =>
  typeof (el as HTMLElement).animate === 'function';

const nodes = (root: HTMLElement, kind: Anim): HTMLElement[] =>
  Array.from(root.querySelectorAll<HTMLElement>(`[data-anim="${kind}"]`));

const cancelAll = (root: HTMLElement) => {
  (['title', 'table', 'thead', 'row'] as Anim[]).forEach((kind) =>
    nodes(root, kind).forEach((n) => {
      if (supportsAnimate(n)) {
        n.getAnimations().forEach((a) => a.cancel());
      }
    }),
  );
};

const SETTLED = { opacity: 1, transform: 'none', filter: 'blur(0px)' };

export const playIn = ({
  root,
  animateTitle,
}: {
  root: HTMLElement | null;
  animateTitle: boolean;
}) => {
  if (!root) {
    return;
  }
  cancelAll(root);
  const run = (
    n: HTMLElement,
    from: Keyframe,
    delay: number,
    duration: number,
  ) => {
    if (!supportsAnimate(n)) {
      return;
    }
    n.animate([from, SETTLED], {
      duration,
      delay,
      easing: EASE_IN,
      fill: 'backwards',
    });
  };

  nodes(root, 'title').forEach((n) => {
    if (!animateTitle || !supportsAnimate(n)) {
      return;
    }
    n.animate(
      [
        { transform: 'translateY(120%)', filter: 'blur(6px)', opacity: 0.4 },
        {
          transform: 'translateY(-8%)',
          filter: 'blur(0px)',
          opacity: 1,
          offset: 0.7,
        },
        { transform: 'none', filter: 'blur(0px)', opacity: 1 },
      ],
      {
        duration: 560,
        easing: 'cubic-bezier(0.2, 0.9, 0.3, 1)',
        fill: 'backwards',
      },
    );
  });

  const gate = 420;
  nodes(root, 'table').forEach((n) =>
    run(n, { opacity: 0, transform: 'translateY(12px)' }, gate, 500),
  );
  nodes(root, 'thead').forEach((n) =>
    run(n, { opacity: 0, transform: 'translateY(6px)' }, gate + 80, 450),
  );
  // Row cells share one grid, so stagger by their data-row index (not DOM order)
  // to keep a per-row cascade.
  nodes(root, 'row').forEach((n) => {
    const ri = Number(n.getAttribute('data-row')) || 0;
    run(
      n,
      { opacity: 0, transform: 'translateX(40px)', filter: 'blur(4px)' },
      gate + 150 + ri * 40,
      550,
    );
  });
};

export const playOut = ({
  root,
  animateTitle,
}: {
  root: HTMLElement | null;
  animateTitle: boolean;
}) => {
  if (!root) {
    return;
  }
  cancelAll(root);
  const out = (
    n: HTMLElement,
    to: Keyframe,
    delay: number,
    duration: number,
  ) => {
    if (!supportsAnimate(n)) {
      return;
    }
    n.animate([SETTLED, to], {
      duration,
      delay,
      easing: EASE_OUT,
      fill: 'forwards',
    });
  };

  const rows = nodes(root, 'row');
  let maxRow = 0;
  rows.forEach((n) => {
    const ri = Number(n.getAttribute('data-row')) || 0;
    maxRow = Math.max(maxRow, ri);
    out(
      n,
      { opacity: 0, transform: 'translateX(-32px)', filter: 'blur(4px)' },
      ri * 18,
      280,
    );
  });
  const tail = (maxRow + 1) * 18;
  nodes(root, 'thead').forEach((n) => out(n, { opacity: 0 }, tail, 240));
  nodes(root, 'table').forEach((n) =>
    out(n, { opacity: 0, transform: 'translateY(8px)' }, tail + 40, 260),
  );
  if (animateTitle) {
    nodes(root, 'title').forEach((n) =>
      out(
        n,
        { opacity: 0.4, transform: 'translateY(-120%)', filter: 'blur(6px)' },
        tail + 120,
        300,
      ),
    );
  }
};
